import { useState, useEffect, useCallback, useMemo } from "react";
import { Flame, CheckCircle, TrendingUp, Activity } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  1: 'from-gray-500 to-gray-600', 2: 'from-emerald-500 to-emerald-600',
  3: 'from-blue-500 to-blue-600', 4: 'from-violet-500 to-violet-600',
  5: 'from-amber-500 to-amber-600', 6: 'from-rose-500 to-rose-600',
  7: 'from-fuchsia-500 to-fuchsia-600', 8: 'from-cyan-500 to-cyan-600',
  9: 'from-orange-500 to-orange-600', 10: 'from-yellow-400 to-yellow-500',
};

const metricFields = [
  { key: "mood", label: "Mood", color: "text-yellow-500" },
  { key: "stress_level", label: "Stress", color: "text-red-500" },
  { key: "focus_level", label: "Focus", color: "text-blue-500" },
  { key: "confidence", label: "Confidence", color: "text-green-500" },
  { key: "sleep_quality", label: "Sleep", color: "text-purple-500" },
  { key: "performance_rating", label: "Performance", color: "text-orange-500" },
];

export function ProgressSummary() {
  const [gameProfile, setGameProfile] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [wellnessTrends, setWellnessTrends] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [gpRes, mRes, tRes] = await Promise.all([
        axios.get(`${API}/gamification/profile`, { headers }),
        axios.get(`${API}/performance/metrics?days=30`, { headers }),
        fetch(`${API}/wellness/trends/individual?days=7`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setGameProfile(gpRes.data);
      setMetrics(mRes.data);
      if (tRes.ok) setWellnessTrends(await tRes.json());
    } catch (err) { console.error(err.message); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('checkin-complete', handler);
    return () => window.removeEventListener('checkin-complete', handler);
  }, [fetchData]);

  const radarData = useMemo(() => {
    if (!wellnessTrends || wellnessTrends.length === 0) return [];
    const avg = (key) => wellnessTrends.reduce((s, d) => s + (d[key] || 0), 0) / wellnessTrends.length;
    return [
      { metric: 'Mood', value: +avg('mood').toFixed(1), fullMark: 10 },
      { metric: 'Energy', value: +avg('energy').toFixed(1), fullMark: 10 },
      { metric: 'Confidence', value: +avg('confidence').toFixed(1), fullMark: 10 },
      { metric: 'Focus', value: +avg('focus').toFixed(1), fullMark: 10 },
      { metric: 'Sleep', value: +avg('sleep_quality').toFixed(1), fullMark: 10 },
      { metric: 'Performance', value: +avg('performance_rating').toFixed(1), fullMark: 10 },
      { metric: 'Low Stress', value: +(10 - avg('stress')).toFixed(1), fullMark: 10 },
    ];
  }, [wellnessTrends]);

  const overallScore = radarData.length > 0 ? (radarData.reduce((s, d) => s + d.value, 0) / radarData.length).toFixed(1) : '0';

  if (!gameProfile) return null;

  const gp = gameProfile;
  const levelGradient = LEVEL_COLORS[gp.level?.level] || LEVEL_COLORS[1];
  const recent7 = metrics.slice(0, 7);
  const prev7 = metrics.slice(7, 14);
  const totalEntries = metrics.length;
  const consistencyPct = totalEntries >= 7 ? 100 : totalEntries > 0 ? Math.min(100, Math.round((totalEntries / 7) * 100)) : 0;
  const grade = consistencyPct >= 85 ? 'A' : consistencyPct >= 70 ? 'B' : consistencyPct >= 50 ? 'C' : consistencyPct >= 30 ? 'D' : 'F';
  const gradeColor = consistencyPct >= 85 ? 'text-emerald-400' : consistencyPct >= 70 ? 'text-blue-400' : consistencyPct >= 50 ? 'text-amber-400' : 'text-rose-400';
  const daysChecked = Math.min(totalEntries, 7);

  return (
    <div className="space-y-4" data-testid="progress-summary">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-5 h-5 text-emerald-600" />
        <h2 className="text-lg font-bold text-gray-900">Your Progress</h2>
      </div>

      {/* Top row: Streak+Consistency | Level */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid="plans-streak" className="rounded-xl bg-white border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center justify-around gap-4">
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-1.5 ${
                gp.streak.current >= 7 ? 'bg-orange-100' : gp.streak.current >= 3 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                <Flame className={`w-6 h-6 ${gp.streak.current >= 7 ? 'text-orange-500' : gp.streak.current >= 3 ? 'text-amber-500' : 'text-gray-400'}`} />
              </div>
              <p className="text-2xl font-black text-gray-900">{gp.streak.current}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Day Streak</p>
              <p className="text-[9px] text-gray-400">Best: {gp.streak.max}</p>
            </div>
          </div>
          {gp.streak.checked_in_today ? (
            <div className="mt-3 text-center">
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full font-semibold">
                <CheckCircle className="w-3 h-3" /> Checked in today
              </span>
            </div>
          ) : (
            <div className="mt-3 text-center">
              <span className="inline-flex text-[10px] text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full font-semibold">Check in today!</span>
            </div>
          )}
        </div>

        <div data-testid="plans-level" className="rounded-xl bg-white border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${levelGradient} flex items-center justify-center shadow-md flex-shrink-0`}>
              <span className="text-2xl font-black text-white">{gp.level?.level}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Level {gp.level?.level}</span>
                <span className={`text-sm font-bold bg-gradient-to-r ${levelGradient} bg-clip-text text-transparent`}>{gp.level?.title}</span>
              </div>
              <p className="text-xl font-black text-gray-900">{gp.level?.total_xp} <span className="text-xs font-medium text-gray-400">XP</span></p>
              <div className="mt-1.5">
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full bg-gradient-to-r ${levelGradient} transition-all duration-1000`} style={{ width: `${gp.level?.progress_pct || 0}%` }} />
                </div>
                <div className="flex justify-between mt-0.5">
                  <span className="text-[10px] text-gray-400">{gp.level?.xp_in_level} / {gp.level?.xp_for_next_level} XP</span>
                  {gp.level?.next_level && <span className="text-[10px] text-gray-400">Next: {gp.level.next_level.title}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My Mental Performance Profile: Radar + Daily Metrics in ONE box */}
      <div data-testid="plans-mental-perf-profile" className="rounded-xl bg-white border border-emerald-100 p-5 shadow-sm">
        <h3 className="text-sm font-bold text-gray-900 mb-3">My Mental Performance Profile</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Radar */}
          <div className="flex flex-col items-center">
            {radarData.length > 0 ? (
              <>
                <div className="text-center mb-1">
                  <span className="text-xl font-bold text-emerald-600">{overallScore}</span>
                  <span className="text-xs text-gray-500">/10</span>
                  <p className="text-[10px] text-gray-400">7-day avg</p>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart cx="50%" cy="50%" outerRadius="72%" data={radarData}>
                    <PolarGrid stroke="#d1d5db" />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 9, fill: '#374151', fontWeight: 600 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return <div className="bg-white rounded-lg shadow-lg border px-3 py-2 text-xs"><p className="font-bold">{d.metric}</p><p className="text-emerald-600">{d.value}/10</p></div>;
                    }} />
                    <Radar dataKey="value" stroke="#059669" fill="#10b981" fillOpacity={0.25} strokeWidth={2} dot={{ r: 3, fill: '#059669' }} />
                  </RadarChart>
                </ResponsiveContainer>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">Complete check-ins to see your profile</div>
            )}
          </div>

          {/* Daily Metrics with Trends */}
          <div>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Weekly Averages</h4>
            <div className="grid grid-cols-3 gap-2">
              {metricFields.map((field) => {
                const recentAvg = recent7.length > 0
                  ? (recent7.reduce((sum, m) => sum + (m[field.key] || 0), 0) / recent7.length).toFixed(1) : "—";
                const prevAvg = prev7.length > 0
                  ? prev7.reduce((sum, m) => sum + (m[field.key] || 0), 0) / prev7.length : null;
                const trend = prevAvg !== null && recentAvg !== "—" ? parseFloat(recentAvg) - prevAvg : null;
                const isStress = field.key === "stress_level";
                const trendPositive = trend !== null && (isStress ? trend < -0.3 : trend > 0.3);
                const trendNegative = trend !== null && (isStress ? trend > 0.3 : trend < -0.3);
                return (
                  <div key={field.key} className="text-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                    <p className="text-[9px] text-gray-400 uppercase tracking-wider font-bold mb-0.5">{field.label}</p>
                    <p className={`text-base font-black ${field.color}`}>{recentAvg}</p>
                    {trend !== null && (
                      <div className={`flex items-center justify-center gap-0.5 text-[9px] font-bold ${
                        trendPositive ? 'text-emerald-500' : trendNegative ? 'text-rose-500' : 'text-gray-400'}`}>
                        {trendPositive ? <TrendingUp className="w-2.5 h-2.5" /> : trendNegative ? <TrendingUp className="w-2.5 h-2.5 rotate-180" /> : null}
                        {Math.abs(trend).toFixed(1)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {recent7.length === 0 && <p className="text-center text-gray-400 text-sm py-4">Complete check-ins to see averages</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
